/**
 * AgentChatPanel — built on AI Elements patterns
 * Renders conversation thread, tool results, previews, and prompt input.
 */
import React, { useEffect, useRef, useState } from "react";
import { RotateCcw, ChevronRight, Sparkles } from "lucide-react";
import { useIsSmallViewportHeight } from "@/hooks/use-viewport-height";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import {
  Conversation,
  ConversationContent,
} from "@/components/ai-elements/conversation";
import {
  Message,
  MessageContent,
  MessageResponse,
  ToolResult,
  PreviewResult,
  translateAgentLoadingTraceStep,
} from "@/components/ai-elements/message";
import { PromptInput } from "@/components/ai-elements/prompt-input";
import { Suggestions, Suggestion } from "@/components/ai-elements/suggestion";
import { FormInputCard } from "@/components/ai-elements/form-input";
import { LensRecommendationCard } from "@/components/ai-elements/lens-recommendation";
import type {
  ChatMessage,
  ChatMessagePartPreview,
  ChatMessagePartToolResult,
  ChatMessagePartFormInput,
  ChatMessagePartLensRequest,
} from "../useAgent";
import type { AgentLensRecommendation, AgentToolResult } from "@/core/ai/agent/IAgentTool";

/**
 * A rich starter for the empty-state chat: a tappable card with an icon,
 * a verb-first label, and a one-line hint. Replaces flat string chips so the
 * empty state teaches what the assistant can do and feels inviting. The
 * `prompt` is what gets sent on click.
 */
export interface StarterCard {
  prompt: string;
  label: string;
  hint?: string;
  icon?: React.ElementType;
  tone?: "info" | "warning" | "success" | "purple" | "neutral";
}

const STARTER_TONE: Record<NonNullable<StarterCard["tone"]>, string> = {
  info: "bg-blue-50 text-blue-600 dark:bg-blue-950/50 dark:text-blue-300",
  warning: "bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-300",
  success: "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-300",
  purple: "bg-purple-50 text-purple-600 dark:bg-purple-950/50 dark:text-purple-300",
  neutral: "bg-muted text-muted-foreground",
};

interface AgentChatPanelProps {
  renderToolResultAction?: (toolName: string, result: AgentToolResult) => React.ReactNode;
  messages: ChatMessage[];
  isLoading: boolean;
  loadingTrace?: string[];
  liveThinkingText?: string;
  activePreviewMessageId: string | null;
  activeFormMessageId?: string | null;
  activeLensMessageId?: string | null;
  onSend: (text: string) => void;
  onSuggestionSelect?: (text: string) => void;
  onCancel: () => void;
  onClear: () => void;
  onConfirmPreview?: () => void;
  onRejectPreview: () => void;
  onSubmitForm?: (formattedMessage: string, rawValues: Record<string, string>) => void;
  onApplyLens?: (selected: AgentLensRecommendation[]) => void;
  isEmptyDiagram: boolean;
  /** When genogram, use family-oriented placeholder instead of org chart copy. */
  emptyChatContext?: 'org' | 'genogram';
  suggestions?: string[];
  /**
   * Rich empty-state starters (icon + label + hint). When provided and the
   * thread is empty, they replace the flat string suggestion chips with
   * tappable cards. Falls back to `suggestions` for post-message chips.
   */
  starterCards?: StarterCard[];
  showStarterExample?: boolean;
  /**
   * Suppress the built-in empty-state "How can I help?" heading + description.
   * Use when the host already shows a contextual help header above the chat
   * (e.g. Compass `CompassChatHelper`) so the panel doesn't render a second one.
   */
  hideEmptyStateHeader?: boolean;
  /** Optional override for the empty-state heading shown when no messages exist. */
  emptyStateTitle?: string;
  /** Optional override for the empty-state description shown under the heading. */
  emptyStateDescription?: string;
  /** Optional override for the prompt input placeholder when no messages exist. */
  emptyStatePlaceholder?: string;
  /** Optional override when suggestions are visible after messages exist. */
  suggestionsPlaceholder?: string;
  prefillInput?: string | null;
  /**
   * Fires once after `prefillInput` has been written into the input. Lets
   * callers clear their prefill state synchronously instead of guessing a
   * setTimeout — needed so re-clicking the same chip / re-dropping the same
   * file applies the prefill again.
   */
  onPrefillConsumed?: () => void;
  /**
   * Monotonic counter — incrementing it focuses the prompt input (with a
   * focus pulse). Lets a host imperatively focus the chat box, e.g. when the
   * org-chart "Generate from text" CTA opens the assistant.
   */
  focusSignal?: number;
  onInputExpandedChange?: (isExpanded: boolean) => void;
  disabled?: boolean;
  compact?: boolean;
  contextSummary?: string;
  showAnalysisToggle?: boolean;
  translateSuggestionDisplay?: (suggestion: string) => string;
  translateAssistantMessageDisplay?: (text: string) => string;
  onOpenDetailedView?: (detail: {
    toolName: string;
    additions: number;
    removals: number;
    moves: number;
    updates: number;
  }) => void;
}

export const AgentChatPanel: React.FC<AgentChatPanelProps> = ({
  renderToolResultAction,
  messages,
  isLoading,
  loadingTrace = [],
  liveThinkingText = "",
  activePreviewMessageId,
  activeFormMessageId,
  activeLensMessageId,
  onSend,
  onSuggestionSelect,
  onCancel,
  onClear,
  onConfirmPreview,
  onRejectPreview,
  onSubmitForm,
  onApplyLens,
  isEmptyDiagram,
  emptyChatContext = 'org',
  hideEmptyStateHeader = false,
  suggestions: customSuggestions,
  starterCards,
  showStarterExample = true,
  emptyStateTitle: customEmptyStateTitle,
  emptyStateDescription: customEmptyStateDescription,
  emptyStatePlaceholder: customEmptyStatePlaceholder,
  suggestionsPlaceholder,
  prefillInput,
  onPrefillConsumed,
  focusSignal = 0,
  onInputExpandedChange,
  disabled = false,
  compact = false,
  contextSummary,
  showAnalysisToggle = true,
  translateSuggestionDisplay,
  translateAssistantMessageDisplay,
  onOpenDetailedView,
}) => {
  const { t } = useTranslation("diagram");
  const [input, setInput] = useState("");
  const lastAppliedPrefillRef = useRef<string | null>(null);
  const [prefillFocusToken, setPrefillFocusToken] = useState(0);
  const [isAnalysisVisible, setIsAnalysisVisible] = useState(false);
  const [suggestionOffset, setSuggestionOffset] = useState(0);
  const [isSuggestionPending, setIsSuggestionPending] = useState(false);
  const MIN_SUGGESTION_LOADING_MS = 900;
  const isSmallViewport = useIsSmallViewportHeight();

  const handleSubmit = () => {
    if (disabled) return;
    const trimmed = input.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setInput("");
  };

  const handleSuggestionClick = async (suggestion: string) => {
    if (disabled) return;
    if (suggestion === "Show AI analysis") {
      setIsAnalysisVisible(true);
      return;
    }
    if (onSuggestionSelect) {
      setIsSuggestionPending(true);
      const startedAt = performance.now();
      try {
        await onSuggestionSelect(suggestion);
      } finally {
        const elapsed = performance.now() - startedAt;
        if (elapsed < MIN_SUGGESTION_LOADING_MS) {
          await new Promise((resolve) =>
            window.setTimeout(resolve, MIN_SUGGESTION_LOADING_MS - elapsed),
          );
        }
        setIsSuggestionPending(false);
      }
      return;
    }
    onSend(suggestion);
  };

  const shouldShowThinking = isLoading || isSuggestionPending;
  const loadingFresh = t("assistant.chatLoadingFreshOptions", {
    defaultValue: "Loading fresh options",
  });
  const thinkingTrace = isLoading ? loadingTrace : [loadingFresh];
  const thinkingText = isLoading ? liveThinkingText : loadingFresh;
  const thinkingTraceDisplay = thinkingTrace.map((step) =>
    translateAgentLoadingTraceStep(step, t),
  );
  const lastThinkingTraceDisplay =
    thinkingTraceDisplay[thinkingTraceDisplay.length - 1] ?? "";

  const hasMessages = messages.length > 0;
  const hasUserMessage = messages.some((m) => m.role === "user");
  const hasActivePreview = activePreviewMessageId != null;
  const hasActiveForm = activeFormMessageId != null;
  const hasActiveLens = activeLensMessageId != null;
  const activeSuggestions = (hasActiveForm || hasActiveLens) ? [] : (customSuggestions ?? []);

  // Context-aware titles/descriptions. Callers can override per-domain so the
  // empty state speaks the domain's vocabulary instead of the i18n default.
  const defaultEmptyStateTitle = isEmptyDiagram
    ? (t("assistant.chatStartPlanning", { defaultValue: "Start planning" }) as string)
    : (t("assistant.chatHowCanHelp", { defaultValue: "How can I help?" }) as string);
  const defaultEmptyStateDescription = isEmptyDiagram
    ? (t("assistant.chatStartPlanningSubtitle", {
        defaultValue: "Choose a starting point or describe what you want to build",
      }) as string)
    : (t("assistant.chatAskMeSubtitle", {
        defaultValue: "Ask me to filter, color, search, or restructure",
      }) as string);
  const emptyStateTitle = customEmptyStateTitle ?? defaultEmptyStateTitle;
  const emptyStateDescription = customEmptyStateDescription ?? defaultEmptyStateDescription;
  const canShowAnalysisToggle =
    showAnalysisToggle && Boolean(contextSummary) && !hasMessages;
  const hideEmptyScaffold =
    !hasMessages &&
    hideEmptyStateHeader &&
    !canShowAnalysisToggle;
  // Rich starter cards take over the empty state when provided.
  const showStarterCards =
    !hasMessages && (starterCards?.length ?? 0) > 0;

  const normalizePrompt = (value: string) => value.trim().toLowerCase();
  const sentPromptSet = new Set(
    messages
      .filter((m) => m.role === "user")
      .map((m) => m.parts.find((p) => p.type === "text"))
      .filter((p): p is { type: "text"; text: string } => p?.type === "text")
      .map((p) => normalizePrompt(p.text)),
  );

  const adaptiveSuggestions = activeSuggestions.filter((s) => {
    const normalized = normalizePrompt(s);
    if (normalized.startsWith("more ideas")) return false;
    return !sentPromptSet.has(normalized);
  });
  const suggestionsWithActions = [
    ...(canShowAnalysisToggle && !isAnalysisVisible ? ["Show AI analysis"] : []),
    ...adaptiveSuggestions,
  ];

  const maxVisibleSuggestions = isSmallViewport
    ? 2
    : hasMessages
      ? (hasUserMessage ? 4 : 5)
      : 3;
  const hasSuggestionPager = suggestionsWithActions.length > maxVisibleSuggestions;
  const visibleSuggestions = suggestionsWithActions.length <= maxVisibleSuggestions
    ? suggestionsWithActions
    : Array.from({ length: maxVisibleSuggestions }, (_, index) => {
        const safeIndex = (suggestionOffset + index) % suggestionsWithActions.length;
        return suggestionsWithActions[safeIndex];
      });
  const suggestionPage = suggestionsWithActions.length === 0
    ? 0
    : Math.floor(suggestionOffset / maxVisibleSuggestions) + 1;
  const suggestionPages = suggestionsWithActions.length === 0
    ? 0
    : Math.ceil(suggestionsWithActions.length / maxVisibleSuggestions);
  const SHOW_ANALYSIS_PROMPT = "Show AI analysis";
  const hasShowAnalysisSuggestion = visibleSuggestions.includes(SHOW_ANALYSIS_PROMPT);
  const regularSuggestions = visibleSuggestions.filter((s) => s !== SHOW_ANALYSIS_PROMPT);

  useEffect(() => {
    setSuggestionOffset(0);
  }, [hasMessages, isSmallViewport, suggestionsWithActions.length, maxVisibleSuggestions]);

  const cycleSuggestions = () => {
    if (!hasSuggestionPager) return;
    setSuggestionOffset((current) =>
      (current + maxVisibleSuggestions) % suggestionsWithActions.length,
    );
  };

  const suggestionChipDisplayText = (s: string) => {
    if (translateSuggestionDisplay) {
      return translateSuggestionDisplay(s);
    }
    if (s === SHOW_ANALYSIS_PROMPT) {
      return t("assistant.chatShowAiAnalysis", {
        defaultValue: SHOW_ANALYSIS_PROMPT,
      }) as string;
    }
    return s;
  };

  const defaultEmptyStatePlaceholder = isEmptyDiagram
    ? emptyChatContext === 'genogram'
      ? (t("assistant.chatPlaceholderDescribeGenogram", {
          defaultValue: "Map your family…",
        }) as string)
      : (t("assistant.chatPlaceholderDescribeOrg", {
          defaultValue: "Map your organization…",
        }) as string)
    : (t("assistant.chatPlaceholderAskFilter", {
        defaultValue: "Ask to filter, color, search, or restructure…",
      }) as string);
  const emptyStatePlaceholder = customEmptyStatePlaceholder ?? defaultEmptyStatePlaceholder;
  const inputPlaceholder = hasActivePreview
    ? (t("assistant.chatPlaceholderRequestChanges", {
        defaultValue: "Request changes to this proposal…",
      }) as string)
    : hasMessages && suggestionsWithActions.length > 0
      ? (suggestionsPlaceholder ??
        (t("assistant.chatPlaceholderTypeExact", {
          defaultValue: "Type exact requirement, or pick a suggestion…",
        }) as string))
      : hasMessages
        ? (t("assistant.chatPlaceholderReplyNext", {
            defaultValue: "Reply with the next step…",
          }) as string)
        : emptyStatePlaceholder;
  const multiLinePlaceholderThreshold = hasMessages && !isLoading
    ? 28
    : compact
      ? 34
      : 42;
  const promptMinRows = inputPlaceholder.length > multiLinePlaceholderThreshold ? 2 : 1;

  useEffect(() => {
    const next = typeof prefillInput === "string" ? prefillInput.trim() : "";
    if (!next) {
      // When the parent clears the prefill, drop the dedupe ref too so the
      // next non-empty value re-applies even if it matches the previous one.
      lastAppliedPrefillRef.current = null;
      return;
    }
    if (lastAppliedPrefillRef.current === next) return;
    setInput(next);
    setPrefillFocusToken((current) => current + 1);
    lastAppliedPrefillRef.current = next;
    onPrefillConsumed?.();
  }, [prefillInput, onPrefillConsumed]);

  return (
    <div className="flex flex-col min-h-0 flex-1">
      {/* Group: conversation + suggestions — grows to fill space so prompt stays at bottom */}
      <div
        className={cn(
          "min-h-0 flex flex-col overflow-hidden",
          compact && !hasMessages ? "shrink-0" : "flex-1",
          compact ? "pb-2 gap-1.5" : "pb-3 gap-2",
        )}
      >
        {/* Conversation: when empty, fill space and center; when has messages, content-sized and scrolls */}
        <div
          className={cn(
            "min-h-0 max-h-full overflow-y-auto",
            hasMessages
              ? "flex-1"
              : compact
                ? "shrink-0"
              : "flex-1 flex flex-col justify-center items-center",
          )}
        >
          {hasMessages ? (
            <Conversation className="min-h-0 grow-0">
          <ConversationContent>
            {messages.map((message) => (
              <Message key={message.id} from={message.role}>
                <MessageContent>
                  {message.parts.map((part, index) => {
                    if (part.type === "text") {
                      // Needs-input cards already show the full guidance beside
                      // their recovery action. Keep other assistant text intact.
                      if (message.role === "assistant" && message.parts.some(other =>
                        other.type === "tool-result" &&
                        other.result.data?.needsInput === true &&
                        other.result.summary?.trim() === part.text.trim()
                      )) return null;
                      const displayText =
                        message.role === "assistant" && translateAssistantMessageDisplay
                          ? translateAssistantMessageDisplay(part.text)
                          : message.role === "user" && translateSuggestionDisplay
                            ? translateSuggestionDisplay(part.text)
                            : part.text;
                      return (
                        <MessageResponse key={index}>
                          {displayText}
                        </MessageResponse>
                      );
                    }
                    if (part.type === "tool-result") {
                      const toolPart = part as ChatMessagePartToolResult;
                      if (toolPart.toolName === "suggest" || toolPart.toolName === "collect_inputs" || toolPart.toolName === "recommend_view") return null;
                      return (
                        <div key={index}>
                          <ToolResult
                            name={toolPart.toolName}
                            success={toolPart.result.success}
                            summary={toolPart.result.summary}
                            needsInput={toolPart.result.data?.needsInput === true}
                          />
                          {renderToolResultAction?.(toolPart.toolName, toolPart.result)}
                        </div>
                      );
                    }
                    if (part.type === "preview") {
                      const previewPart = part as ChatMessagePartPreview;
                      if (!previewPart.result.preview) return null;
                      const { delta } = previewPart.result.preview;
                      const isActive = activePreviewMessageId === message.id;
                      // Preview Apply/Reject routes through useAgent, then
                      // AgentToolRegistry. The registry owns command and
                      // external-effect execution for preview actions.
                      return (
                        <PreviewResult
                          key={index}
                          name={previewPart.toolName}
                          additions={delta.additions.length}
                          removals={delta.removals.length}
                          moves={delta.moves.length}
                          updates={delta.updates.length}
                          isActive={isActive}
                          onConfirm={onConfirmPreview}
                          onReject={onRejectPreview}
                          onModify={() =>
                            setInput("Change the previous proposal to also...")
                          }
                          onOpenDetailedView={onOpenDetailedView ? () => onOpenDetailedView({
                            toolName: previewPart.toolName,
                            additions: delta.additions.length,
                            removals: delta.removals.length,
                            moves: delta.moves.length,
                            updates: delta.updates.length,
                          }) : undefined}
                        />
                      );
                    }
                    if (part.type === "form-input") {
                      const formPart = part as ChatMessagePartFormInput;
                      if (!formPart.result.formRequest) return null;
                      return (
                        <FormInputCard
                          key={index}
                          formRequest={formPart.result.formRequest}
                          isActive={activeFormMessageId === message.id}
                          onSubmit={onSubmitForm ?? (() => {})}
                        />
                      );
                    }
                    if (part.type === "lens-request") {
                      const lensPart = part as ChatMessagePartLensRequest;
                      if (!lensPart.result.lensRequest) return null;
                      return (
                        <LensRecommendationCard
                          key={index}
                          lensRequest={lensPart.result.lensRequest}
                          isActive={activeLensMessageId === message.id}
                          onApply={onApplyLens ?? (() => {})}
                        />
                      );
                    }
                    return null;
                  })}
                </MessageContent>
              </Message>
            ))}
            {shouldShowThinking && (
              <div className="flex justify-start">
                {thinkingTrace.length > 0 ? (
                  <div className="px-1 py-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[#4a5f6f]" />
                      <span>
                        {thinkingText || lastThinkingTraceDisplay}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 px-1 py-2">
                    <span className="inline-block h-1 w-1 animate-pulse rounded-full bg-[#04263d]" />
                    <span className="inline-block h-1 w-1 animate-pulse rounded-full bg-[#4a5f6f] [animation-delay:150ms]" />
                    <span className="inline-block h-1 w-1 animate-pulse rounded-full bg-[#8b9caa] [animation-delay:300ms]" />
                  </div>
                )}
              </div>
            )}
          </ConversationContent>
            </Conversation>
          ) : showStarterCards ? (
            <div className="flex w-full flex-col gap-3 px-1 pt-1 pb-2">
              {!hideEmptyStateHeader && (
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-950/50 dark:text-blue-300">
                    <Sparkles className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-sm font-medium leading-tight text-foreground">{emptyStateTitle}</span>
                </div>
              )}
              {!hideEmptyStateHeader && emptyStateDescription && (
                <div className="text-xs leading-snug text-muted-foreground">{emptyStateDescription}</div>
              )}
              <div className="flex flex-col gap-1.5">
                {(starterCards ?? []).map((card) => {
                  const Icon = card.icon;
                  return (
                    <button
                      key={card.prompt}
                      type="button"
                      onClick={() => { void handleSuggestionClick(card.prompt); }}
                      disabled={disabled}
                      className="group flex w-full items-center gap-2.5 rounded-md border border-border bg-background px-2.5 py-2 text-left transition-colors hover:border-border/80 hover:bg-accent/50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-background"
                    >
                      {Icon && (
                        <span className={cn("inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md", STARTER_TONE[card.tone ?? "neutral"])}>
                          <Icon className="h-3.5 w-3.5" />
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs font-medium leading-tight text-foreground">{card.label}</span>
                        {card.hint && (
                          <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">{card.hint}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : hideEmptyScaffold ? null : (
            <div className={cn("flex w-full flex-col items-center gap-1 px-4 text-center", compact ? "py-2" : "py-8")}>
              {!hideEmptyStateHeader && (
                <span
                  className={cn(
                    "inline-flex shrink-0 items-center justify-center rounded-full bg-indigo-50 text-indigo-500 dark:bg-indigo-950/40 dark:text-indigo-300",
                    compact ? "mb-1 h-7 w-7" : "mb-2 h-9 w-9",
                  )}
                  aria-hidden
                >
                  <Sparkles className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
                </span>
              )}
              {!hideEmptyStateHeader && (
                <div className="text-sm font-medium text-foreground">
                  {emptyStateTitle}
                </div>
              )}
              {!hideEmptyStateHeader && emptyStateDescription && (
                <div className={cn("text-xs text-muted-foreground", compact ? "max-w-[255px] leading-snug" : "max-w-[220px]")}>
                  {emptyStateDescription}
                </div>
              )}
              {canShowAnalysisToggle && isAnalysisVisible && (
                <div className="mt-1 max-w-[220px] rounded-md bg-muted/40 px-2 py-1.5 text-xs text-muted-foreground">
                  {contextSummary}
                </div>
              )}

            </div>
          )}
        </div>

        {/* Suggestions group — always above input, separate from empty-state helper text */}
        {!shouldShowThinking && !showStarterCards && suggestionsWithActions.length > 0 && (
          <div className={cn("shrink-0 min-w-0 overflow-x-hidden px-1 flex flex-col", compact ? "gap-1" : "gap-1.5")}>
            <Suggestions className="items-center">
              {regularSuggestions.map((s) => (
                <Suggestion
                  key={s}
                  suggestion={s}
                  displayText={suggestionChipDisplayText(s)}
                  onClick={handleSuggestionClick}
                  variant={hasMessages ? "cta" : "neutral"}
                  className={
                    cn(
                      "w-full min-w-0 whitespace-normal break-words text-left",
                      hasUserMessage ? "text-xs leading-tight" : undefined,
                    )
                  }
                />
              ))}
            </Suggestions>
            {(hasShowAnalysisSuggestion || hasSuggestionPager) && (
              <div className="flex items-center justify-between gap-2">
                {hasShowAnalysisSuggestion ? (
                  <Suggestion
                    suggestion={SHOW_ANALYSIS_PROMPT}
                    displayText={suggestionChipDisplayText(SHOW_ANALYSIS_PROMPT)}
                    onClick={handleSuggestionClick}
                    variant={hasMessages ? "cta" : "neutral"}
                    className={cn(
                      "min-w-0 whitespace-normal break-words text-left",
                      hasUserMessage ? "text-xs leading-tight" : undefined,
                    )}
                  />
                ) : (
                  <span />
                )}
                {hasSuggestionPager && (
                  <button
                    type="button"
                    onClick={cycleSuggestions}
                    className="assistant-suggestion assistant-suggestion-text inline-flex max-w-full shrink-0 items-center gap-1.5 rounded-md bg-slate-50 px-2 py-0.5 text-[11px] font-normal transition-colors hover:bg-muted dark:border dark:border-slate-600/50 dark:bg-slate-800/80 dark:text-slate-200"
                  >
                    <span>
                      {suggestionPages > 1
                        ? t("assistant.chatMoreIdeasNumbered", {
                            page: suggestionPage,
                            pages: suggestionPages,
                            defaultValue: `More ideas (${suggestionPage}/${suggestionPages})`,
                          })
                        : t("assistant.chatMoreIdeas", {
                            defaultValue: "More ideas",
                          })}
                    </span>
                    <ChevronRight className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Prompt input — always at bottom */}
      <div className="shrink-0 flex items-end gap-1.5">
        <div className="flex-1 min-w-0">
          <PromptInput
            value={input}
            onChange={setInput}
            onSubmit={handleSubmit}
            onCancel={onCancel}
            onExpandedChange={onInputExpandedChange}
            focusSignal={prefillFocusToken + focusSignal}
            isLoading={isLoading}
            disabled={disabled}
            placeholder={inputPlaceholder}
            compact={compact}
            minRows={promptMinRows}
          />
        </div>
        {hasMessages && !isLoading && (
          <button
            onClick={onClear}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors mb-0.5"
            aria-label={t("assistant.chatNewChat", { defaultValue: "New chat" })}
            title={t("assistant.chatNewChat", { defaultValue: "New chat" })}
          >
            <svg
              width="0"
              height="0"
              className="absolute"
              aria-hidden="true"
              focusable="false"
            >
              <defs>
                <linearGradient
                  id="assistant-gradient-stroke"
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#6466E9" />
                  <stop offset="100%" stopColor="#3D9DF2" />
                </linearGradient>
              </defs>
            </svg>
            <RotateCcw
              className="h-4 w-4"
              color="url(#assistant-gradient-stroke)"
            />
          </button>
        )}
      </div>
      {showStarterCards && showStarterExample && starterCards?.[0] && (
        <div className="shrink-0 px-1 pt-1.5 text-center text-[11px] leading-snug text-muted-foreground">
          {t("assistant.chatTryExample", { defaultValue: "try" })}: <span className="italic">"{starterCards[0].prompt}"</span>
        </div>
      )}
    </div>
  );
};
